import { useState } from 'react'
import type { BuzonHilo as Hilo, BuzonMensaje } from '@/lib/types'
import { Badge } from '@/components/ui'
import { sanitizeHtml } from '@/lib/utils'
import { nombreDe, emailDe, fechaLarga } from '@/lib/correo-utils'
import {
  IconArrowLeft, IconMail, IconPaperclip, IconAlertTriangle, IconCheckCheck,
} from '@/components/icons/Icons'
import BuzonResponder from './BuzonResponder'

/*
  Panel de lectura de un hilo del Buzón de Equipo.

  No se reusa `CorreoHilo` (el de las fichas) a propósito: ese muestra texto
  plano recortado y tiene «quitar del proyecto», este muestra el HTML del
  correo y responde contra otro endpoint. Generalizar los dos sería un
  componente con dos modos — peor que dos componentes.
*/
interface Props {
  hilo: Hilo
  detalle: Hilo | null
  onVolver: () => void
  onRespondido: () => void
}

/** Lo que pasó con un mensaje saliente. `null` = entrante, no aplica. */
function estadoBadge(m: BuzonMensaje) {
  if (m.direccion !== 'saliente' || !m.estado_envio) return null
  // Se reusan las variantes que ya existen en el design system en vez de
  // inventar tokens nuevos: el significado (bien / mal / ojo) ya está cubierto.
  switch (m.estado_envio) {
    case 'entregado': return <Badge variant="completado"><IconCheckCheck size={10} /> Entregado</Badge>
    case 'rebotado': return <Badge variant="rechazada"><IconAlertTriangle size={10} /> Rebotó</Badge>
    case 'spam': return <Badge variant="pausado"><IconAlertTriangle size={10} /> Spam</Badge>
    case 'fallido': return <Badge variant="rechazada"><IconAlertTriangle size={10} /> Falló</Badge>
    default: return <Badge variant="info">Enviado</Badge>
  }
}

function Mensaje({ mensaje, abiertoPorDefecto }: { mensaje: BuzonMensaje; abiertoPorDefecto: boolean }) {
  const [abierto, setAbierto] = useState(abiertoPorDefecto)
  const esNuestro = mensaje.direccion === 'saliente'

  return (
    <div className={`correo-msg${esNuestro ? ' correo-msg--propio' : ''}`}>
      <div className="correo-msg__head" onClick={() => setAbierto(!abierto)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAbierto(!abierto) } }}>
        <div>
          <div className="correo-msg__de">
            {nombreDe(mensaje.de)}
            {estadoBadge(mensaje)}
          </div>
          <div className="correo-msg__para">
            para {mensaje.para.map(nombreDe).join(', ') || '—'}
            {mensaje.cc.length > 0 && ` · cc ${mensaje.cc.map(nombreDe).join(', ')}`}
          </div>
          {!abierto && mensaje.cuerpo_texto && (
            <div className="correo-msg__resumen">{mensaje.cuerpo_texto.slice(0, 160)}</div>
          )}
        </div>
        <span className="correo-msg__fecha">{fechaLarga(mensaje.fecha)}</span>
      </div>

      {abierto && (
        <>
          {/* Regla del proyecto: todo dangerouslySetInnerHTML pasa por sanitizeHtml.
              Acá además es cuerpo de correo entrante, o sea HTML de afuera. */}
          <div
            className="correo-msg__texto"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(mensaje.cuerpo_html || `<pre>${mensaje.cuerpo_texto || ''}</pre>`),
            }}
          />
          {mensaje.adjuntos?.length > 0 && (
            <div className="correo-msg__adjuntos">
              {mensaje.adjuntos.map((a, i) => (
                // Sin enlace: del adjunto solo se guarda el nombre y el tipo.
                // Bajarlos a R2 es fase posterior (hay que decidir retención).
                <span key={i} className="correo-adjunto">
                  <IconPaperclip size={12} /> {a.nombre}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default function BuzonHilo({ hilo, detalle, onVolver, onRespondido }: Props) {
  const mensajes = detalle?.mensajes ?? []
  const externos = (hilo.participantes || []).filter(
    (p) => emailDe(p) !== (hilo.remitente_email || '').toLowerCase(),
  )

  return (
    <div className="correos-hilo">
      <div className="correo-hilo__header">
        <button className="btn btn-ghost btn-sm correos-volver" onClick={onVolver}>
          <IconArrowLeft size={14} /> Volver
        </button>
        <div>
          <div className="correo-hilo__titulo">{hilo.asunto || '(sin asunto)'}</div>
          <div className="correo-hilo__sub">
            {externos.length ? externos.join(', ') : '—'}
            {hilo.remitente_nombre && ` · desde ${hilo.remitente_nombre}`}
          </div>
        </div>
      </div>

      <div className="correo-hilo__cuerpo">
        {!detalle ? (
          <p className="text-muted" style={{ padding: 16 }}>Cargando el hilo…</p>
        ) : mensajes.length === 0 ? (
          <div className="correos-vacio">
            <IconMail size={26} />
            <p>Este hilo no tiene mensajes.</p>
          </div>
        ) : (
          mensajes.map((m, i) => (
            // El último abierto, el resto plegado: es lo que se quiere leer al
            // entrar, y un hilo de doce mensajes abierto entero es ilegible.
            <Mensaje key={m.id} mensaje={m} abiertoPorDefecto={i === mensajes.length - 1} />
          ))
        )}
      </div>

      {detalle && mensajes.length > 0 && (
        <BuzonResponder hilo={detalle} onEnviado={onRespondido} />
      )}
    </div>
  )
}
